import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { WA_LOCAL_STORAGE, WA_WINDOW } from '@ng-web-apis/common';
import { RmaOverlayService } from '@rma/generic/ui/overlay';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { PlatformService } from '@rma/public/generic/util/environment';
import { FeatureFlags, FeatureFlagService } from '@rma/public/generic/util/feature-flags';
import { Angulartics2 } from 'angulartics2';
import { catchError, concatMap, defer, EMPTY, filter, forkJoin, fromEvent, map, merge, Observable, of, switchMap, take, timer } from 'rxjs';
import { ConsultationOfferComponent } from './consultation-offer.component';
import {
  CONSULTATION_OFFER_CATEGORY,
  CONSULTATION_OFFER_DWELL_MS,
  CONSULTATION_OFFER_EVENTS,
  CONSULTATION_OFFER_REPEAT_AFTER_MS,
  CONSULTATION_OFFER_SCROLL_RATIO,
  CONSULTATION_OFFER_SHOWN_AT_KEY,
  ConsultationOfferEngagement,
  isConsultationOfferPath,
} from './domain/consultation-offer.model';

@Injectable({
  providedIn: 'root',
})
export class ConsultationOfferService {
  private readonly overlayService = inject(RmaOverlayService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly userContext = inject(PublicUserContextService);
  private readonly platformService = inject(PlatformService);
  private readonly analytics = inject(Angulartics2);
  private readonly localStorage = inject<Storage>(WA_LOCAL_STORAGE);
  private readonly window = inject<Window>(WA_WINDOW);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  public initialise(): void {
    if (this.platformService.isPlatformServer) {
      return;
    }

    if (this.platformService.isPlatformBrowserBot) {
      return;
    }

    const shownAt = Number(this.localStorage.getItem(CONSULTATION_OFFER_SHOWN_AT_KEY));

    if (Date.now() - shownAt < CONSULTATION_OFFER_REPEAT_AFTER_MS) {
      return;
    }

    // Deferred so a flag service that throws on the way in reaches catchError like any other failure.
    defer(() => this.featureFlagService.getFeature$(FeatureFlags.FindMyAgentPopupBanner, false))
      .pipe(
        take(1),
        filter((flagOn) => flagOn),
        // Arming re-runs per arrival: a wait started on one route must never open the card on the
        // next, and a route that does not qualify arms nothing without ending the session.
        switchMap(() => this.routeArrivals$()),
        map((_, index) => index + 1),
        switchMap((navigationCount) => this.armRoute(navigationCount)),
        take(1),
        // An error anywhere above leaves the card unopened and the visitor undisturbed.
        catchError(() => EMPTY),
      )
      .subscribe((engagement) => {
        this.localStorage.setItem(CONSULTATION_OFFER_SHOWN_AT_KEY, String(Date.now()));
        this.openCard(engagement);
      });
  }

  private routeArrivals$(): Observable<unknown> {
    const navigations$ = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Seeded when the router has already navigated: the landing route's NavigationEnd has been and
    // gone by the time the session gates finish, and that route must arm like any other.
    return this.router.navigated ? merge(of(null), navigations$) : navigations$;
  }

  private armRoute(navigationCount: number): Observable<ConsultationOfferEngagement> {
    return defer(() => (isConsultationOfferPath(this.router.url) ? this.waitForEngagement(navigationCount) : EMPTY)).pipe(
      // Read here rather than at arm time: a session that begins signed-out stays eligible, and a
      // session still resolving its user must not be read as signed-out.
      concatMap((engagement) =>
        this.userContext.isAuthenticated$.pipe(
          take(1),
          filter((isAuthenticated) => !isAuthenticated),
          map(() => engagement),
        ),
      ),
      // An error on one arrival leaves that route without a card; later arrivals still arm.
      catchError(() => EMPTY),
    );
  }

  private waitForEngagement(navigationCount: number): Observable<ConsultationOfferEngagement> {
    return forkJoin([timer(CONSULTATION_OFFER_DWELL_MS), this.engagement$(navigationCount)]).pipe(map(([, engagement]) => engagement));
  }

  private engagement$(navigationCount: number): Observable<ConsultationOfferEngagement> {
    // A session-level count, not a per-route event: a visitor who has already moved through the site
    // has shown intent, so only the dwell on this route is still owed.
    if (navigationCount > 1) {
      return of<ConsultationOfferEngagement>('second-navigation');
    }

    return fromEvent(this.window, 'scroll', { passive: true }).pipe(
      filter(() => this.scrollDepth() >= CONSULTATION_OFFER_SCROLL_RATIO),
      map((): ConsultationOfferEngagement => 'scroll-depth'),
      take(1),
    );
  }

  private scrollDepth(): number {
    const element = this.document.scrollingElement ?? this.document.documentElement;
    const scrollable = element.scrollHeight - element.clientHeight;

    if (scrollable <= 0) {
      return 0;
    }

    return element.scrollTop / scrollable;
  }

  private openCard(engagement: ConsultationOfferEngagement): void {
    const { overlayRef, contentRef } = this.overlayService.open(null, ConsultationOfferComponent, {
      hasBackdrop: false,
      // Spans the overlay container rather than 100vw, which would include the scrollbar gutter and
      // let the mobile bar overhang. The card handles its own width and right-alignment from there.
      width: '100%',
      // Neither a focus trap nor a scroll lock: the card is an offer, not a decision, and the page
      // stays readable and scrollable behind it.
      scrollStrategy: this.overlayService.overlay.scrollStrategies.noop(),
      positionStrategy: this.overlayService.overlay.position().global().bottom('0'),
    });

    if (!('instance' in contentRef)) {
      overlayRef.dispose();
      return;
    }

    this.track(CONSULTATION_OFFER_EVENTS.shown, engagement);

    contentRef.instance.dismissed.subscribe(() => {
      this.track(CONSULTATION_OFFER_EVENTS.dismissed, engagement);
      overlayRef.dispose();
    });

    // Fired on the click rather than after it: the anchor unloads the document, and no in-app route
    // change follows that a later hook could ride.
    contentRef.instance.accepted.subscribe(() => this.track(CONSULTATION_OFFER_EVENTS.clicked, engagement));
  }

  private track(action: string, engagement: ConsultationOfferEngagement): void {
    const path = this.router.url;

    // Under `category`/`label`/`gstCustom`: the GA4 forwarder reads only its own keys off the
    // properties and drops anything flat beside them.
    this.analytics.eventTrack.next({
      action,
      properties: { category: CONSULTATION_OFFER_CATEGORY, label: path, gstCustom: { engagement, path } },
    });
  }
}
